// Página pública de Fixture y Resultados del Mundial 2026
import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { Calendar, Clock, Search, Filter, ShieldAlert } from 'lucide-react'
import client from '../api/client'
import { parseBoliviaDate } from '../api/dateUtils'
import Card from '../components/ui/Card'
import Badge from '../components/ui/Badge'
import Flag from '../components/ui/Flag'
import Skeleton from '../components/ui/Skeleton'
import AdCarousel from '../components/ui/AdCarousel'

const FASES = [
  { id: 'todos', label: 'Todo el Torneo' },
  { id: 'grupos', label: 'Fase de Grupos' },
  { id: 'eliminatorias', label: 'Rondas Eliminatorias' }
]

const ELIMINATORIAS_RONDAS = [
  { id: 'todos', label: 'Todas las Rondas' },
  { id: 'dieciseisavos', label: '16avos' },
  { id: 'octavos', label: 'Octavos' },
  { id: 'cuartos', label: 'Cuartos' },
  { id: 'semifinal', label: 'Semifinales' },
  { id: 'tercer_puesto', label: '3er Puesto' },
  { id: 'final', label: 'Final' }
]

const GRUPOS = ['Todos', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L']

export default function WorldCupMatches() {
  const [activeFaseTab, setActiveFaseTab] = useState('grupos')
  const [selectedGroup, setSelectedGroup] = useState('Todos')
  const [selectedRonda, setSelectedRonda] = useState('todos')
  const [searchTerm, setSearchTerm] = useState('')

  // Obtener partidos desde la API
  const { data: matches = [], isLoading, error } = useQuery({
    queryKey: ['publicMatches'],
    queryFn: async () => {
      const response = await client.get('/matches')
      return response.data?.datos || []
    },
    // Refresco automático de 30 segundos si hay partidos en juego
    refetchInterval: (query) => {
      const partidos = query.state.data || []
      return partidos.some(m => m.estado === 'en_juego') ? 30000 : false
    }
  })

  // Obtener publicidades desde la API
  const { data: ads = [] } = useQuery({
    queryKey: ['activeAds-internas'],
    queryFn: async () => {
      const response = await client.get('/ads?ubicacion=internas')
      return response.data?.datos || []
    }
  })

  // Filtrar y ordenar partidos
  const filteredMatches = useMemo(() => {
    let result = [...matches]

    // 1. Filtrar por Fase Principal
    if (activeFaseTab === 'grupos') {
      result = result.filter(m => m.fase === 'grupos')
      
      // Filtrar por Grupo específico
      if (selectedGroup !== 'Todos') {
        result = result.filter(m => m.grupo_nombre === selectedGroup)
      }
    } else if (activeFaseTab === 'eliminatorias') {
      result = result.filter(m => m.fase !== 'grupos')

      // Filtrar por Ronda eliminatoria específica
      if (selectedRonda !== 'todos') {
        result = result.filter(m => m.fase === selectedRonda)
      }
    }

    // 2. Filtrar por término de búsqueda (nombre de equipo)
    if (searchTerm.trim() !== '') {
      const query = searchTerm.toLowerCase()
      result = result.filter(m => 
        (m.equipo_local_nombre || '').toLowerCase().includes(query) ||
        (m.equipo_visitante_nombre || '').toLowerCase().includes(query)
      )
    }

    // Ordenar cronológicamente
    return result.sort((a, b) => parseBoliviaDate(a.fecha_partido) - parseBoliviaDate(b.fecha_partido))
  }, [matches, activeFaseTab, selectedGroup, selectedRonda, searchTerm])

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-12 w-64 rounded-xl" />
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <Skeleton className="h-10 w-96 rounded-lg" />
          <Skeleton className="h-10 w-64 rounded-lg" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-44 rounded-2xl" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto text-center py-20">
        <div className="w-16 h-16 bg-error/10 text-error rounded-2xl flex items-center justify-center mx-auto mb-4 border border-error/20">
          <ShieldAlert className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-display font-bold text-text-primary mb-2">Error al cargar el fixture</h2>
        <p className="text-sm text-text-secondary">No pudimos conectar con el servidor para obtener los partidos.</p>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-4">
      {/* Banner Publicitario */}
      <AdCarousel ads={ads} variant="banner-only" />

      {/* Encabezado */}
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-display font-bold text-text-primary">
          Fixture & Resultados 🏆
        </h1>
        <p className="text-text-secondary mt-1.5">
          Calendario oficial de los partidos del Mundial 2026. Todos los horarios se muestran en hora de Bolivia (BOT, UTC-4).
        </p>
      </div>

      {/* Barra de Filtros y Búsqueda */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Selector de fase */}
        <div className="flex bg-surface-card rounded-xl p-1 border border-border-subtle col-span-1 lg:col-span-2 overflow-x-auto shrink-0">
          {FASES.map(f => (
            <button
              key={f.id}
              onClick={() => {
                setActiveFaseTab(f.id)
                setSelectedGroup('Todos')
                setSelectedRonda('todos')
              }}
              className={`flex-1 min-w-[120px] py-2 px-3 rounded-lg text-xs font-semibold font-display transition-all whitespace-nowrap ${
                activeFaseTab === f.id
                  ? 'bg-cyan text-navy-dark shadow-md shadow-cyan/15'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-card-hover'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Buscador */}
        <div className="relative">
          <Search className="w-4 h-4 text-text-muted absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Buscar por país..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-surface-card border border-border-subtle text-text-primary placeholder:text-text-muted text-sm focus:outline-none focus:border-cyan/50 focus:bg-surface-card-hover transition-colors font-body"
          />
        </div>
      </div>

      {/* Selectores Secundarios */}
      <AnimatePresence mode="wait">
        {activeFaseTab === 'grupos' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6"
          >
            <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-thin scrollbar-thumb-border-subtle scrollbar-track-transparent">
              {GRUPOS.map(g => (
                <button
                  key={g}
                  onClick={() => setSelectedGroup(g)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold font-body border transition-all whitespace-nowrap shrink-0 ${
                    selectedGroup === g
                      ? 'bg-cyan/15 border-cyan text-cyan'
                      : 'bg-surface-card border-border-subtle text-text-secondary hover:border-border-default hover:text-text-primary'
                  }`}
                >
                  {g === 'Todos' ? 'Todos los Grupos' : `Grupo ${g}`}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {activeFaseTab === 'eliminatorias' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mb-6"
          >
            <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-thin scrollbar-thumb-border-subtle scrollbar-track-transparent">
              {ELIMINATORIAS_RONDAS.map(r => (
                <button
                  key={r.id}
                  onClick={() => setSelectedRonda(r.id)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold font-body border transition-all whitespace-nowrap shrink-0 ${
                    selectedRonda === r.id
                      ? 'bg-cyan/15 border-cyan text-cyan'
                      : 'bg-surface-card border-border-subtle text-text-secondary hover:border-border-default hover:text-text-primary'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Grid de Partidos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredMatches.map(match => {
          const isFinished = match.estado === 'finalizado'
          const isLive = match.estado === 'en_juego'
          const isScheduled = match.estado === 'programado'
          const parsedDate = parseBoliviaDate(match.fecha_partido)

          return (
            <motion.div
              key={match.id}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="h-full flex flex-col justify-between border border-border-subtle hover:border-cyan/30 bg-surface-card/40 hover:bg-surface-card/60 transition-all duration-300 relative overflow-hidden group">
                {/* Glow decorativo */}
                <div className="absolute top-0 left-0 w-full h-[2px] bg-transparent group-hover:bg-gradient-to-r group-hover:from-transparent group-hover:via-cyan/50 group-hover:to-transparent transition-colors" />

                {/* Meta y Estado */}
                <div className="flex items-center justify-between p-4 border-b border-border-subtle text-xs text-text-muted">
                  <span className="font-mono uppercase bg-navy-dark/60 px-2 py-0.5 rounded border border-border-subtle font-semibold">
                    {match.fase === 'grupos' ? `Grupo ${match.grupo_nombre}` : match.fase.replace('_', ' ').toUpperCase()}
                  </span>
                  
                  {isFinished && (
                    <Badge variant="success" size="sm" dot>Finalizado</Badge>
                  )}
                  {isLive && (
                    <Badge variant="warning" size="sm" dot className="animate-pulse">En vivo</Badge>
                  )}
                  {isScheduled && (
                    <Badge variant="default" size="sm">Programado</Badge>
                  )}
                </div>

                {/* Scoreboard */}
                <div className="flex-1 flex items-center justify-between p-6 gap-3">
                  {/* Local */}
                  <div className="flex-1 flex flex-col items-center text-center space-y-2">
                    <Flag code={match.equipo_local_bandera} className="w-12 h-8 object-cover rounded shadow-md border border-border-subtle" />
                    <span className="font-display font-bold text-sm text-text-primary line-clamp-1">
                      {match.equipo_local_nombre}
                    </span>
                  </div>

                  {/* Marcador central */}
                  <div className="flex flex-col items-center justify-center shrink-0 min-w-[64px]">
                    {isFinished ? (
                      <div className="flex items-center gap-1.5 bg-navy-dark/80 px-3 py-1.5 rounded-xl border border-border-subtle font-mono text-lg font-bold text-cyan">
                        <span>{match.goles_local}</span>
                        <span className="text-text-muted text-sm font-body font-normal">-</span>
                        <span>{match.goles_visitante}</span>
                      </div>
                    ) : isLive ? (
                      <div className="flex flex-col items-center">
                        <div className="flex items-center gap-1.5 bg-warning/10 px-3 py-1.5 rounded-xl border border-warning/20 font-mono text-lg font-bold text-warning">
                          <span>{match.goles_local}</span>
                          <span className="text-text-muted text-xs font-body font-normal">-</span>
                          <span>{match.goles_visitante}</span>
                        </div>
                        <span className="text-[9px] text-warning font-semibold tracking-widest mt-1 uppercase animate-pulse">En Juego</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center text-center bg-navy-dark/40 border border-border-subtle py-1 px-2.5 rounded-lg">
                        <span className="text-cyan font-mono text-sm font-semibold">VS</span>
                      </div>
                    )}
                  </div>

                  {/* Visitante */}
                  <div className="flex-1 flex flex-col items-center text-center space-y-2">
                    <Flag code={match.equipo_visitante_bandera} className="w-12 h-8 object-cover rounded shadow-md border border-border-subtle" />
                    <span className="font-display font-bold text-sm text-text-primary line-clamp-1">
                      {match.equipo_visitante_nombre}
                    </span>
                  </div>
                </div>

                {/* Fecha y Hora del partido */}
                <div className="bg-navy-dark/20 p-4 border-t border-border-subtle flex items-center justify-between text-xs text-text-muted">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-cyan shrink-0" />
                    <span className="font-medium">
                      {parsedDate.toLocaleDateString('es-ES', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short'
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-cyan shrink-0" />
                    <span className="font-mono font-semibold text-text-secondary">
                      {parsedDate.toLocaleTimeString('es-ES', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })} BOT
                    </span>
                  </div>
                </div>
              </Card>
            </motion.div>
          )
        })}

        {filteredMatches.length === 0 && (
          <div className="col-span-full py-16 text-center">
            <p className="text-text-muted text-sm">No se encontraron partidos para los filtros aplicados.</p>
          </div>
        )}
      </div>
    </div>
  )
}
